import { ChevronDown, LogOut, User } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/common/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/common/components/ui/avatar';
import { avatarUrl } from '@/common/utils/avatarUrl';
import { AccountService } from '@/features/auth/services/AccountService.ts';
import { redirectToLogin } from '@/features/auth/utils/redirectToLogin.ts';

export function UserMenu() {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  const firstName = currentUser.firstName ?? '';
  const lastName = currentUser.lastName ?? '';
  const displayName =
    [firstName, lastName].filter(Boolean).join(' ') || currentUser.username || currentUser.email;

  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .join('');

  const avatarEl = (
    <Avatar className="h-8 w-8 shrink-0 border border-border">
      <AvatarImage src={avatarUrl(firstName, lastName, currentUser.email)} alt={displayName} />
      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="user menu"
        >
          {avatarEl}
          <div className="hidden flex-col items-start md:flex">
            <span className="text-sm font-medium text-foreground leading-tight">{displayName}</span>
            <span className="text-xs text-muted-foreground leading-tight">{currentUser.email}</span>
          </div>
          <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center gap-3 px-3 py-2">
          {avatarEl}
          <div className="flex flex-col">
            <span className="text-sm font-medium">{displayName}</span>
            <span className="text-xs text-muted-foreground">{currentUser.email}</span>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate({ to: '/profile' })}>
          <User className="mr-2 h-4 w-4" />
          <Trans>My Profile</Trans>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => {
            AccountService.logout().finally(() => redirectToLogin());
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <Trans>Logout</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
